import type {
  AppointmentNoteResponse,
  AppointmentNoteSaveRequest,
  AppointmentNoteSaveResponse,
} from "../../../shared/api-types";
import {
  canonicalJson,
  cloneRichTextDocument,
  type RichTextDocument,
} from "../../../shared/rich-text-document";

export const APPOINTMENT_NOTE_AUTOSAVE_DELAY_MS = 750;

export type AppointmentNoteSaveState = "saved" | "saving" | "failed" | "conflict";
type AppointmentNoteAutosavePhase = "idle" | "scheduled" | "saving" | "failed" | "conflict";

export interface AppointmentNoteAutosaveSnapshot {
  document: RichTextDocument;
  status: AppointmentNoteSaveState;
  dirty: boolean;
  conflict: AppointmentNoteResponse | null;
}

export type AppointmentNoteSaveOutcome =
  | { status: "saved"; value: AppointmentNoteSaveResponse }
  | { status: "conflict"; current: AppointmentNoteResponse };

interface AppointmentNoteAutosaveOptions {
  createSaveId?: () => string;
  schedule?: (run: () => void, delay: number) => ReturnType<typeof setTimeout>;
  cancel?: (timer: ReturnType<typeof setTimeout>) => void;
  onError?: (error: unknown) => void;
}

export class AppointmentNoteAutosave {
  private document: RichTextDocument;
  private savedDocument: RichTextDocument;
  private baseRevisionId: string | null;
  private phase: AppointmentNoteAutosavePhase = "idle";
  private conflict: AppointmentNoteResponse | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private pendingRequest: AppointmentNoteSaveRequest | null = null;
  private disposed = false;
  private readonly createSaveId: () => string;
  private readonly schedule: NonNullable<AppointmentNoteAutosaveOptions["schedule"]>;
  private readonly cancel: NonNullable<AppointmentNoteAutosaveOptions["cancel"]>;
  private readonly onError: (error: unknown) => void;

  constructor(
    initial: AppointmentNoteResponse,
    private readonly save: (request: AppointmentNoteSaveRequest) => Promise<AppointmentNoteSaveOutcome>,
    private readonly changed: (snapshot: AppointmentNoteAutosaveSnapshot) => void,
    options: AppointmentNoteAutosaveOptions = {},
  ) {
    this.document = cloneRichTextDocument(initial.document);
    this.savedDocument = cloneRichTextDocument(this.document);
    this.baseRevisionId = initial.revision?.id ?? null;
    this.createSaveId = options.createSaveId ?? (() => crypto.randomUUID());
    const schedule = options.schedule;
    const cancel = options.cancel;
    this.schedule = schedule
      ? (run, delay) => schedule(run, delay)
      : (run, delay) => globalThis.setTimeout(run, delay);
    this.cancel = cancel
      ? (timer) => cancel(timer)
      : (timer) => globalThis.clearTimeout(timer);
    this.onError = options.onError ?? (() => {});
  }

  snapshot(): AppointmentNoteAutosaveSnapshot {
    return {
      document: cloneRichTextDocument(this.document),
      status: this.saveState(),
      dirty: this.hasUnsavedWork(),
      conflict: this.conflict,
    };
  }

  edit(document: RichTextDocument): void {
    if (this.disposed) return;
    this.document = cloneRichTextDocument(document);
    if (this.phase === "conflict" || this.phase === "saving") {
      this.emit();
      return;
    }
    if (this.phase === "failed") {
      this.emit();
      return;
    }
    this.queueAutosave();
  }

  retry(): void {
    if (this.disposed || this.phase !== "failed") return;
    void this.flush(true);
  }

  useLatest(): RichTextDocument | null {
    if (this.disposed || !this.conflict) return null;
    this.document = cloneRichTextDocument(this.conflict.document);
    this.savedDocument = cloneRichTextDocument(this.document);
    this.baseRevisionId = this.conflict.revision?.id ?? null;
    this.pendingRequest = null;
    this.conflict = null;
    this.phase = "idle";
    this.emit();
    return cloneRichTextDocument(this.document);
  }

  saveMine(): void {
    if (this.disposed || !this.conflict) return;
    this.baseRevisionId = this.conflict.revision?.id ?? null;
    this.pendingRequest = null;
    this.conflict = null;
    this.phase = "idle";
    void this.flush(true);
  }

  dispose(): void {
    this.disposed = true;
    if (this.timer) this.cancel(this.timer);
    this.timer = null;
  }

  private queueAutosave(): void {
    if (this.disposed) return;
    if (this.timer) this.cancel(this.timer);
    if (!this.isDirty()) {
      this.timer = null;
      this.phase = "idle";
      this.emit();
      return;
    }
    this.phase = "scheduled";
    this.timer = this.schedule(() => {
      this.timer = null;
      void this.flush();
    }, APPOINTMENT_NOTE_AUTOSAVE_DELAY_MS);
    this.emit();
  }

  private async flush(force = false): Promise<void> {
    if (this.disposed || this.phase === "saving" || this.phase === "conflict") return;
    if (!this.hasUnsavedWork()) return;
    if (!force && this.phase === "failed") return;
    this.phase = "saving";
    this.emit();

    while (!this.disposed) {
      this.pendingRequest ??= this.createRequest();
      let outcome: AppointmentNoteSaveOutcome;
      try {
        outcome = await this.save(this.pendingRequest);
      } catch (error) {
        if (this.disposed) return;
        this.onError(error);
        this.phase = "failed";
        this.emit();
        return;
      }
      if (this.disposed) return;
      if (outcome.status === "conflict") {
        this.pendingRequest = null;
        this.phase = "conflict";
        this.conflict = outcome.current;
        this.emit();
        return;
      }
      this.savedDocument = cloneRichTextDocument(outcome.value.document);
      this.baseRevisionId = outcome.value.revision?.id ?? null;
      this.pendingRequest = null;
      if (!this.isDirty()) {
        this.phase = "idle";
        this.emit();
        return;
      }
    }
  }

  private createRequest(): AppointmentNoteSaveRequest {
    return {
      document: cloneRichTextDocument(this.document),
      baseRevisionId: this.baseRevisionId,
      saveId: this.createSaveId(),
    };
  }

  private isDirty(): boolean {
    return canonicalJson(this.document) !== canonicalJson(this.savedDocument);
  }

  private hasUnsavedWork(): boolean {
    return this.pendingRequest !== null || this.isDirty();
  }

  private saveState(): AppointmentNoteSaveState {
    switch (this.phase) {
      case "idle": return "saved";
      case "scheduled":
      case "saving": return "saving";
      case "failed": return "failed";
      case "conflict": return "conflict";
    }
  }

  private emit(): void {
    if (this.disposed) return;
    this.changed(this.snapshot());
  }
}
